import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import Button from '@components/Button';
import Input from '@components/Input';

import {
  FETCH_PAYMENT_PORTAL_JWT_TOKEN_SUCCESS,
  getPaymentPortalConfig,
  getPaymentPortalJWTToken,
  getPaymentPortalTransactionListing,
  PAYMENT_PORTAL_CONFIG_FAILURE,
  PAYMENT_PORTAL_TRANSACTION_LISTING_SUCCESS,
} from '@actions/paymentPortal';

import { getEmptyErrorState } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

import Footer from './PaymentPortalFooter';
import Header from './PaymentPortalHeader';
import PaymentPortalListing from './PaymentPortalListing';

import './style.scss';

export interface IPaymentPortalHomeProps extends RouteComponentProps<{ portalToken: string }> {
  // From redux
  config: IPaymentPortalConfig;
  getPaymentPortalConfig: TGetPaymentPortalConfig;
  getPaymentPortalTransactionListing: TGetPaymentPortalTransactionListing;
  getPaymentPortalJWTToken: TGetPaymentPortalJWTToken;
  paymentPortalJWTToken: string;
  paymentPortalBalanceListing: IPaymentPortalBalanceListing;
}

export interface IPaymentPortalHomeState {
  caseNumber: string;
  nameOnNotice: string;
  isBalanceListingPageVisible: boolean;
  error: {
    caseNumber: IFieldValidation;
    nameOnNotice: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

export class PaymentPortalHome extends React.Component<IPaymentPortalHomeProps, IPaymentPortalHomeState> {
  constructor(props: IPaymentPortalHomeProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    const { portalToken } = this.props.match.params;
    this.props.getPaymentPortalConfig(portalToken).then((action) => {
      if (action.type === PAYMENT_PORTAL_CONFIG_FAILURE) {
        this.props.history.replace('/payment-portal/error');
      }
    });
  }

  getEmptyState = (): IPaymentPortalHomeState => ({
    caseNumber: '',
    nameOnNotice: '',
    isBalanceListingPageVisible: false,
    error: {
      caseNumber: getEmptyErrorState(),
      nameOnNotice: getEmptyErrorState(),
    },
  });

  validateForm = () => {
    let isValid = true;
    const { caseNumber, nameOnNotice } = this.state;
    const error = this.getEmptyState().error;

    if (cyberdyneValidator.isEmptyString(caseNumber)) {
      error.caseNumber.errorState = IValidationState.ERROR;
      error.caseNumber.errorMessage = errMsgGen.getMandatoryFieldMessage('Case number');

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(nameOnNotice)) {
      error.nameOnNotice.errorState = IValidationState.ERROR;
      error.nameOnNotice.errorMessage = errMsgGen.getMandatoryFieldMessage('Name on notice');

      isValid = false;
    }

    this.setState({ error });

    return isValid;
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  onCaseSearch = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (this.validateForm()) {
      const { portalToken } = this.props.match.params;
      const { caseNumber, nameOnNotice } = this.state;
      const paymentPortalJWTTokenRequest = {
        caseNumber,
        citizenName: nameOnNotice,
      };
      this.props.getPaymentPortalJWTToken(portalToken, paymentPortalJWTTokenRequest).then((action) => {
        if (action.type === FETCH_PAYMENT_PORTAL_JWT_TOKEN_SUCCESS) {
          this.props.getPaymentPortalTransactionListing(this.props.paymentPortalJWTToken).then((res) => {
            if (res.type === PAYMENT_PORTAL_TRANSACTION_LISTING_SUCCESS) {
              this.setState((prevState) => ({
                // tslint:disable-next-line:max-line-length
                isBalanceListingPageVisible: !prevState.isBalanceListingPageVisible,
              }));
            }
          });
        }
      });
    }
  };

  renderHeader = () => {
    const { agencyLogoUrl, headerBackgroundColor } = this.props.config;

    return <Header logo={agencyLogoUrl} bgColor={headerBackgroundColor} />;
  };

  renderCaseSection = () => {
    const { buttonBackgroundColor, description, noticeImageUrl, title } = this.props.config;

    const { caseNumber, nameOnNotice, error } = this.state;

    return (
      <main className="payment-portal-home__main">
        <section className="payment-info">
          <header className="section-title">
            <h1>{title}</h1>
          </header>
          <div className="payment-description">
            {noticeImageUrl && (
              <div className="notice-sample-image">
                <img src={noticeImageUrl} alt="notice sample" />
              </div>
            )}
            <p>{description}</p>
          </div>
        </section>
        <section>
          <header className="section-title">
            <h1>Enter your case number and your name</h1>
          </header>
          <div className="case-search">
            <div className="case-search-fields">
              <Input
                field={{
                  label: 'Case Number',
                  value: caseNumber,
                  isRequired: true,
                  type: CustomFieldType.TEXT,
                }}
                placeholder="Enter your case number"
                name="caseNumber"
                onChange={this.handleChange}
                error={error.caseNumber}
                width={6}
              />
              <Input
                field={{
                  label: 'Name on Notice',
                  value: nameOnNotice,
                  isRequired: true,
                  type: CustomFieldType.TEXT,
                }}
                placeholder="Enter your full name as it appears on your notice"
                name="nameOnNotice"
                onChange={this.handleChange}
                error={error.nameOnNotice}
                width={6}
              />
            </div>
            <Button
              bsStyle={ButtonStyle.PRIMARY}
              onClick={this.onCaseSearch}
              className="case-search-button"
              content="Search"
              bgColor={buttonBackgroundColor}
            />
          </div>
        </section>
      </main>
    );
  };

  renderFooter = () => {
    const { footerBackgroundColor, originWebsiteUrl, originWebsiteLinkText } = this.props.config;

    return (
      <Footer bgColor={footerBackgroundColor} websiteText={originWebsiteLinkText} websiteLink={originWebsiteUrl} />
    );
  };

  renderPaymentPortalBalanceListing = () => (
    <PaymentPortalListing
      show={this.state.isBalanceListingPageVisible}
      config={this.props.config}
      paymentPortalBalanceListing={this.props.paymentPortalBalanceListing}
      caseNumber={this.state.caseNumber}
      portalToken={this.props.match.params.portalToken}
    />
  );

  render() {
    const { config } = this.props;

    return (
      <div className="payment-portal-home">
        {!!config && (
          <>
            {this.renderHeader()}
            {!this.state.isBalanceListingPageVisible && this.renderCaseSection()}
            {this.props.paymentPortalBalanceListing && this.renderPaymentPortalBalanceListing()}
            {this.renderFooter()}
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  config: state.paymentPortal.config,
  paymentPortalBalanceListing: state.paymentPortal.paymentPortalBalanceListing,
  paymentPortalJWTToken: state.paymentPortal.paymentPortalJWTToken,
});

const mapDispatchToProps = (dispatch: any) => ({
  getPaymentPortalConfig: (token: string) => dispatch(getPaymentPortalConfig(token)),
  getPaymentPortalTransactionListing: (paymentPortalJWTToken: string) =>
    dispatch(getPaymentPortalTransactionListing(paymentPortalJWTToken)),
  getPaymentPortalJWTToken: (token: string, paymentPortalJWTTokenRequest: IPaymentPortalJWTTokenRequest) =>
    dispatch(getPaymentPortalJWTToken(token, paymentPortalJWTTokenRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPortalHome);
